import { useMemo } from 'react';
import {
  ClientDeliveryRequestHistory,
  ClientDeliveryRequestStatus,
  clientRequestPresentableDefaultStatus,
} from 'src/types/public/public-data-model.types';

type ClientDeliveryRequestTimeline = {
  title: string;
  date: string | null;
  isCompleted: boolean;
  isActive: boolean;
  isVisible: boolean;
  isRejected: boolean;
};

export const useComputeClientDeliveryRequests = (
  history: ClientDeliveryRequestHistory[],
): ClientDeliveryRequestTimeline[] => {
  return useMemo(() => {
    const rejectionStatuses: ClientDeliveryRequestStatus[] = ['REJECTED_BY_CUSTOMER', 'REJECTED_BY_AGENCY'];

    // Find the first rejection status in the history
    const rejectionIndex = history.findIndex((entry) => rejectionStatuses.includes(entry.status));

    // Active status is the last completed or the rejection index
    const activeStatusIndex = rejectionIndex >= 0 ? rejectionIndex : history.length;

    return clientRequestPresentableDefaultStatus.map((status, index) => {
      const matchingHistory = history.find((entry) => entry.status === status);
      const isCompleted = matchingHistory !== undefined && index < activeStatusIndex;
      const isActive = matchingHistory !== undefined && index === activeStatusIndex;
      const isRejected = matchingHistory !== undefined && rejectionStatuses.includes(status);

      // Ensure steps before rejection (inclusive) or the current step are visible
      const isVisible = rejectionIndex === -1 || index <= rejectionIndex;
      const includeRejectionStep = index === rejectionIndex && rejectionIndex !== -1;

      return {
        title: status,
        date: matchingHistory ? matchingHistory.createdAt : null,
        isCompleted,
        isActive,
        isRejected,
        isVisible: isVisible || includeRejectionStep,
      };
    });
  }, [history]);
};
